import * as React from 'react';
import { DefaultButton } from '@bb-ui/react-library';
import { Edit } from '@bb-ui/icons/dist/small';
import { useTranslation } from 'react-i18next';
import useRestApi from 'hooks/useRestApi';
import { useSnackbar } from 'hooks/useSnackbar';
import { apiUrl } from 'utils/apiUrl';
import { ReleaseStageTargetVersionDialog } from './ReleaseStageTargetVersionDialog';

export interface ReleaseStageTargetVersionButtonProps {
  releaseStageId: string;
  currentVersion: string;
  lastVersion?: string;
  onUpdated: () => void;
}

export const ReleaseStageTargetVersionButton: React.FC<ReleaseStageTargetVersionButtonProps> = ({
  releaseStageId,
  currentVersion,
  lastVersion,
  onUpdated,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = React.useState(false);
  const {
    clearFailedRequests,
    clearSucceededRequests,
    doPatch,
    failedRequests,
    loadingRequests,
    succeededRequests,
  } = useRestApi(apiUrl('lct', 'releaseStage'), { manual: true });

  const submitting = loadingRequests;

  React.useEffect(() => {
    if (succeededRequests.length === 0) return;

    clearSucceededRequests();
    setOpen(false);
    enqueueSnackbar(t('releaseStageMembers.editTargetVersion.succeeded'), {
      variant: 'success',
    });
    onUpdated();
  }, [clearSucceededRequests, enqueueSnackbar, onUpdated, succeededRequests.length, t]);

  React.useEffect(() => {
    if (failedRequests.length === 0) return;

    clearFailedRequests();
    setOpen(false);
    enqueueSnackbar(t('releaseStageMembers.editTargetVersion.failed'), {
      variant: 'error',
    });
  }, [clearFailedRequests, enqueueSnackbar, failedRequests.length, t]);

  return (
    <>
      <DefaultButton
        startIcon={<Edit />}
        disabled={submitting}
        onClick={() => setOpen(true)}
        data-testid="release-stage-members-edit-target-version"
      >
        {t('releaseStageMembers.actions.editTargetVersion')}
      </DefaultButton>
      <ReleaseStageTargetVersionDialog
        id={`release-stage-members-target-version-${releaseStageId}`}
        open={open}
        currentVersion={currentVersion}
        lastVersion={lastVersion}
        submitting={submitting}
        onConfirm={(learnVersion) => doPatch(releaseStageId, { learnVersion })}
        onClose={() => setOpen(false)}
      />
    </>
  );
};
